/**
 * Which group and tab the app starts on. A deep link that names a tab wins
 * (src/utils/deepLink.ts); otherwise the pair the visitor last had, read back
 * from localStorage and checked against GROUP_TABS, so a tab that no longer
 * exists, or one outside its stored group, falls back to "all". The check used
 * to be a hand-written list that never learnt barrow, megalith, plaque,
 * stolperstein, lighthouse or byleksikon, so a reload on any of those six
 * landed on "all" (found 2 Sep 2026). Read by App.tsx's two useState initialisers.
 */
import type { ActiveTab, CategoryGroup } from "../types/index.ts";
import { GROUP_TABS } from "../constants/index.ts";
import { parseDeepLinkTab } from "./deepLink.ts";

export interface TabState { group: CategoryGroup; tab: ActiveTab }

const GROUPS = new Set<string>(Object.keys(GROUP_TABS));

/** `storage` is injected so the rule can be tested without a DOM. */
export function initialTabState(
  hash: string,
  storage: Pick<Storage, "getItem"> | null,
): TabState {
  const linked = parseDeepLinkTab(hash);
  if (linked) return linked;
  let storedGroup: string | null = null;
  let storedTab: string | null = null;
  try {
    storedGroup = storage?.getItem("fornland-active-group") ?? null;
    storedTab = storage?.getItem("fornland-active-tab") ?? null;
  } catch { /* storage refused: a plain visit */ }
  const group = (storedGroup && GROUPS.has(storedGroup) ? storedGroup : "all") as CategoryGroup;
  // "all" is a tab in every group (the group's own overview); a named tab
  // must belong to the restored group, or the two disagree on screen.
  const tab = storedTab === "all" || !storedTab ? "all"
    : (GROUP_TABS[group] as readonly string[]).includes(storedTab) ? (storedTab as ActiveTab)
    : "all";
  return { group, tab };
}
